import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import { mapState } from 'vuex';
import dtwinApi from '@/services/apiService/digitalTwin';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
export default {
  name: 'TwinFilterEditor',
  props: {
    value: {
      type: Object,
      required: true
    },
    customParameters: {
      type: Object,
      required: true
    },
    dashboardId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      twinSearchString: '',
      parentTwinSearchString: '',
      isSearchingTwins: false,
      isSearchingParentTwins: false,
      twinSearchItems: [],
      parentTwinSearchItems: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    ...mapState('dashboards', ['dashboards']),
    dashboard() {
      return this.dashboards.find(dashboard => dashboard.id === this.dashboardId);
    },
    tags: {
      get() {
        const tags = get(this.value, 'tags', []);
        if (!Array.isArray(tags)) {
          return [];
        }
        return tags;
      },
      set(val) {
        this.$emit('input', {
          ...this.value,
          tags: val
        });
      }
    },
    selectionForParentTwinsInput() {
      const parentTwinIds = get(this.value, 'parentDtwinIds', []);
      return [...this.parentTwinSearchItems, ...this.dashboardParametersAsSelectItems].filter(item => parentTwinIds.includes(String(item.value))).filter((item, index, self) => self.findIndex(t => t.value === item.value) === index);
    },
    selectionForTwinsInput() {
      const twinIds = get(this.value, 'ids', []);
      return [...this.twinSearchItems, ...this.dashboardParametersAsSelectItems].filter(item => twinIds.includes(String(item.value))).filter((item, index, self) => self.findIndex(t => t.value === item.value) === index);
    },
    dashboardParametersAsSelectItems() {
      return Object.keys(this.customParameters).map(key => ({
        value: `{{${key}}}`,
        text: `{{${key}}}`
      }));
    }
  },
  watch: {
    dashboardId: {
      handler() {
        this.searchTwins();
        this.searchParentTwins();
      },
      immediate: true
    },
    twinSearchString: {
      handler() {
        this.searchTwins();
      },
      immediate: true
    },
    'value.ids': {
      handler() {
        this.searchTwins();
      },
      immediate: true
    },
    parentTwinSearchString: {
      handler() {
        this.searchParentTwins();
      },
      immediate: true
    },
    'value.parentDtwinIds': {
      handler() {
        this.searchParentTwins();
      },
      immediate: true
    }
  },
  methods: {
    setByKey(key, val) {
      const clone = cloneDeep(this.value);
      if (key === 'tag') {
        val = val || undefined;
      }
      set(clone, key, val);
      this.$emit('input', clone);
    },
    searchParentTwins: debounce(function () {
      this.actuallySearchTwins('parents');
    }, 500),
    searchTwins: debounce(function () {
      this.actuallySearchTwins('twins');
    }, 500),
    async _fetchTwinsBySearchString(searchString) {
      const twins = [];
      const dashboardTenantId = get(this.dashboard, 'identifier.tenantId', undefined);
      if (!dashboardTenantId) {
        return twins;
      }
      const result = await dtwinApi.digitalTwinFetchPartialTree(this, undefined, searchString, undefined, ['bare'], dashboardTenantId);
      const recursiveAddTwinToTwins = twin => {
        twins.push({
          ...twin,
          children: undefined
        });
        if (Array.isArray(twin.children)) {
          twin.children.forEach(child => {
            recursiveAddTwinToTwins(child);
          });
        }
      };
      result.digitaltwins.forEach(twin => {
        recursiveAddTwinToTwins(twin);
      });
      return twins;
    },
    async actuallySearchParentTwins() {
      this.isSearchingParentTwins = true;
      const twins = [];
      const searchString = this.parentTwinSearchString;
      if ((searchString || '').length > 3) {
        twins.push(...(await this._fetchTwinsBySearchString(searchString)));
      } else {
        const dashboardTenantId = get(this.dashboard, 'identifier.tenantId');
        const dashboardParentTwinId = get(this.dashboard, 'meta.parentDtwinId');
        if (!dashboardTenantId || !dashboardParentTwinId) {
          return twins;
        }
        const result = await dtwinApi.digitalTwinFetchPartialTree(this, dashboardParentTwinId, undefined, undefined, ['bare'], dashboardTenantId);
        const addToTwins = twin => {
          twins.push(twin);
          if (Array.isArray(twin.children)) {
            twin.children.forEach(child => {
              addToTwins(child);
            });
          }
        };
        result.digitaltwins.forEach(twin => {
          addToTwins(twin);
        });
      }
      const searchItems = [...this.dashboardParametersAsSelectItems, ...twins.map(twin => {
        return {
          value: twin.id,
          text: twin.title
        };
      })];
      const previouslySelectedParentTwinIds = get(this.value, 'parentDtwinIds', []).map(Number).filter(val => !isNaN(val));
      if (previouslySelectedParentTwinIds.length > 0) {
        const {
          digitaltwins: previouslySelectedParentTwins
        } = await dtwinApi.digitalTwinsFetch(this, null, [], [{
          type: 'dtwinIds',
          ids: previouslySelectedParentTwinIds
        }]);
        searchItems.push(...previouslySelectedParentTwins.map(twin => ({
          value: twin.id,
          text: twin.title
        })));
      }
      this.parentTwinSearchItems = searchItems;
    },
    async actuallySearchNonParentTwins() {
      this.isSearchingTwins = true;
      let twins = [];
      const searchString = this.twinSearchString;
      if ((searchString || '').length > 3) {
        twins.push(...(await this._fetchTwinsBySearchString(searchString)));
      } else {
        const dashboardTenantId = get(this.dashboard, 'identifier.tenantId');
        const dashboardParentTwinId = get(this.dashboard, 'meta.parentDtwinId');
        if (!dashboardTenantId || !dashboardParentTwinId) {
          return twins;
        }
        const result = await dtwinApi.digitalTwinFetchPartialTree(this, dashboardParentTwinId, undefined, undefined, ['bare'], dashboardTenantId);
        const addToTwins = twin => {
          twins.push(twin);
          if (Array.isArray(twin.children)) {
            twin.children.forEach(child => {
              addToTwins(child);
            });
          }
        };
        result.digitaltwins.forEach(twin => {
          addToTwins(twin);
        });
      }
      const searchItems = [...this.dashboardParametersAsSelectItems, ...twins.map(twin => {
        return {
          value: twin.id,
          text: twin.title
        };
      })];
      const previouslySelectedTwinIds = get(this.value, 'ids', []).map(Number).filter(val => !isNaN(val));
      if (previouslySelectedTwinIds.length > 0) {
        const {
          digitaltwins: previouslySelectedTwins
        } = await dtwinApi.digitalTwinsFetch(this, null, [], [{
          type: 'dtwinIds',
          ids: previouslySelectedTwinIds
        }]);
        searchItems.push(...previouslySelectedTwins.map(twin => ({
          value: twin.id,
          text: twin.title
        })));
      }
      this.twinSearchItems = searchItems;
    },
    async actuallySearchTwins(type) {
      try {
        if (type === 'parents') {
          await this.actuallySearchParentTwins();
        } else if (type === 'twins') {
          await this.actuallySearchNonParentTwins();
        }
      } catch (error) {
        console.error(error);
        this.$snack(this.$t('Dashboards.components.twinFilterEditor.snack.error.couldNotFetchTwins', {
          error: error.message
        }), 'error');
      } finally {
        if (type === 'parents') {
          this.isSearchingParentTwins = false;
        } else if (type === 'twins') {
          this.isSearchingTwins = false;
        }
      }
    },
    setParentTwinSelection(val) {
      const clone = cloneDeep(val);
      this.$emit('input', {
        ...this.value,
        parentDtwinIds: clone.map(String)
      });
    },
    setTwinSelection(val) {
      const clone = cloneDeep(val);
      this.$emit('input', {
        ...this.value,
        ids: clone.map(String)
      });
    }
  }
};